import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { TREATMENTS, METHODS } from './dionData';
import { Reveal } from './DionShared';

const FADE = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } };

/* SCENE 06 — TREATMENT AREAS */
export const TreatmentsScene = () => {
  const [active, setActive] = useState(TREATMENTS[0]);
  return (
    <section id="behandlungen" className="relative bg-[#0a0d14] py-24 lg:py-36" data-testid="dion-treatments">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <Reveal className="mb-12">
          <span className="dh-eyebrow">06 — Behandlungen</span>
          <h2 className="dh-display mt-5 text-[clamp(2rem,5vw,3.6rem)] text-white">Was möchten Sie verändern?</h2>
        </Reveal>

        <div className="grid gap-8 lg:grid-cols-[1fr_360px] lg:gap-12">
          <div className="relative aspect-[4/3] overflow-hidden bg-[#111827] lg:aspect-[16/10]">
            <AnimatePresence mode="wait">
              <motion.img key={active.id} src={active.image} alt={`${active.label} — Symbolbild`} className="dh-media-img absolute inset-0" {...FADE} />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 sm:p-8">
              <AnimatePresence mode="wait">
                <motion.div key={active.id} {...FADE}>
                  <span className="dh-kicker text-[#5b8da8]">{active.focus}</span>
                  <h3 className="dh-display mt-2 text-2xl text-white sm:text-3xl">{active.label}</h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">{active.text}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex flex-col">
            {TREATMENTS.map((t) => {
              const on = t.id === active.id;
              return (
                <button
                  key={t.id} onMouseEnter={() => setActive(t)} onFocus={() => setActive(t)} onClick={() => setActive(t)}
                  data-testid={`treatment-tab-${t.id}`}
                  className="group border-t border-white/10 py-6 text-left last:border-b"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className={`dh-display text-xl transition-colors sm:text-2xl ${on ? 'text-white' : 'text-white/45 group-hover:text-white/80'}`}>{t.label}</span>
                    <ArrowUpRight size={20} className={`transition-all ${on ? 'text-[#5b8da8]' : 'text-white/30'}`} />
                  </div>
                  <div className={`h-px origin-left bg-[#5b8da8] transition-transform duration-500 ${on ? 'scale-x-100' : 'scale-x-0'}`} />
                </button>
              );
            })}
            <p className="dh-kicker mt-6 leading-relaxed text-white/40">Motive sind Symbolbilder und keine Behandlungsergebnisse.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* SCENE 07 — METHODS */
export const MethodsScene = () => {
  const [active, setActive] = useState(METHODS[0]);
  return (
    <section id="methoden" className="relative bg-[#07090e] py-24 lg:py-36" data-testid="dion-methods">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <Reveal className="mb-12">
          <span className="dh-eyebrow">07 — Methoden</span>
          <h2 className="dh-display mt-5 text-[clamp(2rem,5vw,3.6rem)] text-white">Präzisionstechniken.<br /><span className="text-white/55">Individuell gewählt.</span></h2>
        </Reveal>

        <div className="grid gap-10 lg:grid-cols-[420px_1fr] lg:gap-16">
          <div className="flex flex-col">
            {METHODS.map((m, i) => {
              const on = m.id === active.id;
              return (
                <button
                  key={m.id} onClick={() => setActive(m)} onMouseEnter={() => setActive(m)}
                  data-testid={`method-card-${m.id}`}
                  className="group border-t border-white/10 py-6 text-left last:border-b"
                >
                  <div className="flex items-center gap-5">
                    <span className="dh-kicker text-white/35">0{i + 1}</span>
                    <span className={`dh-display text-2xl transition-colors sm:text-3xl ${on ? 'text-white' : 'text-white/40 group-hover:text-white/75'}`}>{m.name}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="relative">
            <div className="relative aspect-[16/10] overflow-hidden bg-[#111827]">
              <AnimatePresence mode="wait">
                <motion.img key={active.id} src={active.image} alt={`${active.name} — Symbolbild`} className="dh-media-img absolute inset-0" {...FADE} />
              </AnimatePresence>
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={active.id} {...FADE} className="mt-8">
                <p className="text-base font-light text-[#5b8da8]">{active.line}</p>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/75">{active.text}</p>
              </motion.div>
            </AnimatePresence>
            <p className="dh-kicker mt-8 leading-relaxed text-white/40">Keine Technik ist pauschal überlegen. Die Auswahl richtet sich nach dem individuellen Behandlungsplan.</p>
          </div>
        </div>
      </div>
    </section>
  );
};
